import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PrimaryActionButton, StatePanel, StonePanel } from '@aztec-realms/ui';

import { useVerticalSliceQuery } from './data/use-vertical-slice';
import {
  fetchEntitlements,
  fetchShopCatalog,
  safeCheckoutUrl,
  type CheckoutResult,
  type ShopProduct,
} from './data/shop-api';
import { beginPlatformCheckout } from './data/platform-checkout';
import { useJourneySafety } from './journey-safety';
import { useNetworkStatus } from './network-status';
import './shop.css';

const catalogKey = ['shop', 'catalog', 'v1'] as const;
const entitlementsKey = ['shop', 'entitlements', 'v1'] as const;

function productName(code: string): string {
  return code
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatShopPrice(product: Pick<ShopProduct, 'currency' | 'priceMinor'>): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: product.currency,
  }).format(product.priceMinor / 100);
}

function checkoutId(productCode: string): string {
  return `shop.checkout:${productCode}:${crypto.randomUUID()}`;
}

function ProductArtwork({ product }: { product: ShopProduct }) {
  const motif = product.code.includes('builder')
    ? 'builder'
    : product.code.includes('codex')
      ? 'codex'
      : 'expedition';

  return (
    <div aria-hidden="true" className="shop-product__art" data-motif={motif}>
      <span className="shop-product__art-orbit" />
      <span className="shop-product__art-object">
        {motif === 'builder' ? (
          <svg focusable="false" viewBox="0 0 96 72">
            <path d="M16 57h64M22 56V38l26-18 26 18v18M31 56V42h12v14m10 0V35h12v21" />
            <path d="M18 33h60M48 20V8m-8 8 8-8 8 8" />
          </svg>
        ) : motif === 'codex' ? (
          <svg focusable="false" viewBox="0 0 96 72">
            <path d="M17 15c13-4 23 0 31 8v38c-8-8-18-12-31-8V15Zm62 0c-13-4-23 0-31 8v38c8-8 18-12 31-8V15Z" />
            <path d="M48 23v38M26 27h13m-13 9h13m18-9h13m-13 9h13" />
            <circle cx="48" cy="14" r="5" />
          </svg>
        ) : (
          <svg focusable="false" viewBox="0 0 96 72">
            <circle cx="48" cy="36" r="25" />
            <circle cx="48" cy="36" r="13" />
            <path d="M48 5v12m0 38v12M17 36H5m86 0H79M26 14l8 10m28 24 8 10m0-44-8 10M34 48 26 58" />
            <path d="m48 27 8 9-8 9-8-9 8-9Z" />
          </svg>
        )}
      </span>
      <small>
        {motif === 'builder'
          ? 'Civic craft'
          : motif === 'codex'
            ? 'Archive adornment'
            : 'Season route'}
      </small>
    </div>
  );
}

function ProductCard({
  product,
  owned,
  disabled,
  pending,
  onCheckout,
}: {
  product: ShopProduct;
  owned: boolean;
  disabled: boolean;
  pending: boolean;
  onCheckout: () => void;
}) {
  return (
    <StonePanel className="shop-product" ornament={product.productType === 'subscription' ? 2 : 1}>
      <ProductArtwork product={product} />
      <div className="shop-product__heading">
        <span aria-hidden="true" className="shop-product__seal">
          {product.productType === 'subscription' ? '☀' : '✦'}
        </span>
        <div>
          <span className="realm-screen__eyebrow">{product.productType.replace('_', ' ')}</span>
          <h2>{productName(product.code)}</h2>
        </div>
      </div>
      <strong className="shop-product__price">{formatShopPrice(product)}</strong>
      <ul>
        {product.benefits.map((benefit) => (
          <li key={benefit}>{benefit}</li>
        ))}
      </ul>
      <p className="shop-product__fairness">
        No competitive power · no external value · server-verified entitlement
      </p>
      <PrimaryActionButton disabled={disabled || owned} loading={pending} onClick={onCheckout}>
        {owned ? 'Already active' : `Choose ${productName(product.code)}`}
      </PrimaryActionButton>
    </StonePanel>
  );
}

function CheckoutStatus({ result }: { result: CheckoutResult }) {
  return (
    <div className="shop-checkout-status" role="status">
      <strong>Checkout prepared by the server.</strong>
      <span>
        Reference {result.purchaseId.slice(0, 8)} · status {result.status}
      </span>
      <small>
        Benefits activate only after the configured provider confirms payment to the server.
      </small>
    </div>
  );
}

export function ShopScreen() {
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const bootstrap = useVerticalSliceQuery();
  const queryClient = useQueryClient();
  const catalog = useQuery({ queryKey: catalogKey, queryFn: fetchShopCatalog });
  const entitlements = useQuery({ queryKey: entitlementsKey, queryFn: fetchEntitlements });
  const checkout = useMutation({
    mutationFn: (productCode: string) =>
      beginPlatformCheckout(window, productCode, checkoutId(productCode)),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: entitlementsKey });
      const url = safeCheckoutUrl(result.checkoutUrl);
      if (url) window.location.assign(url);
    },
  });

  const paymentsEnabled = bootstrap.data?.featureFlags.paymentsEnabled === true;
  const interactionBlocked =
    !online || journey.safety.mode === 'journey-passive' || !paymentsEnabled;
  const ownedCodes = new Set(
    entitlements.data
      ?.filter((entitlement) => entitlement.status === 'active')
      .map((entitlement) => entitlement.code),
  );

  if (catalog.isPending || entitlements.isPending) {
    return (
      <StatePanel kind="loading" title="Opening the lakeside market">
        Confirming the current catalogue and your server-held entitlements.
      </StatePanel>
    );
  }
  if (catalog.isError || entitlements.isError) {
    return (
      <StatePanel
        actionLabel="Try again"
        kind="error"
        onAction={() => {
          void catalog.refetch();
          void entitlements.refetch();
        }}
        title="The market could not open"
      >
        Reconnect and try again. No purchase was started.
      </StatePanel>
    );
  }

  return (
    <section
      className="player-screen shop-screen"
      data-visual-ready="true"
      data-visual-state="shop-pass"
    >
      <header className="shop-hero">
        <span className="realm-screen__eyebrow">Lakeside market · optional expression</span>
        <h1>Explorer Pass & Adornments</h1>
        <p>
          Support the living realm with cosmetic and bounded convenience items. Exploration, Decode
          mastery and Tribe rank always remain earned through play.
        </p>
      </header>

      {journey.safety.mode === 'journey-passive' && (
        <div className="shop-boundary" role="status">
          Journey Mode is passive while you are moving. The shop is locked until you stop safely.
        </div>
      )}
      {!online && (
        <div className="shop-boundary" role="status">
          Purchases are unavailable offline. Reconnect for fresh server-authoritative prices.
        </div>
      )}
      {!paymentsEnabled && online && journey.safety.mode !== 'journey-passive' && (
        <div className="shop-boundary" role="status">
          Payments are currently closed. You can review the catalogue without starting checkout.
        </div>
      )}
      {checkout.isError && (
        <div className="inline-error" role="alert">
          {checkout.error.message}
        </div>
      )}
      {checkout.data && <CheckoutStatus result={checkout.data} />}

      <div className="shop-grid">
        {catalog.data?.map((product) => (
          <ProductCard
            disabled={interactionBlocked}
            key={product.code}
            onCheckout={() => checkout.mutate(product.code)}
            owned={ownedCodes.has(product.entitlementCode)}
            pending={checkout.isPending && checkout.variables === product.code}
            product={product}
          />
        ))}
      </div>

      <footer className="shop-pledge">
        <strong>Fair-play pledge</strong>
        <p>
          Purchases never increase competitive reward-credit multipliers, buy leaderboard rank or
          bypass puzzle mastery. AZC is disabled and absent from checkout.
        </p>
      </footer>
    </section>
  );
}
